import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not already retried, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const { data } = await axios.post(`${API_URL}/auth/refresh`, {}, {
          withCredentials: true,
        });

        if (data.token) {
          localStorage.setItem('token', data.token);
          originalRequest.headers.Authorization = `Bearer ${data.token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, logout user
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/admin/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// API Functions

// Auth
export const login = (credentials) => api.post('/auth/login', credentials);
export const logout = () => api.post('/auth/logout');
export const getMe = () => api.get('/auth/me');
export const updateProfile = (data) => api.put('/auth/profile', data);

// Projects
export const getProjects = (params) => api.get('/projects', { params });
export const getProjectBySlug = (slug) => api.get(`/projects/${slug}`);
export const createProject = (data) => api.post('/projects', data);
export const updateProject = (id, data) => api.put(`/projects/${id}`, data);
export const deleteProject = (id) => api.delete(`/projects/${id}`);
export const toggleProjectFeatured = (id) => api.patch(`/projects/${id}/featured`);

// Services
export const getServices = (params) => api.get('/services', { params });
export const getServiceBySlug = (slug) => api.get(`/services/${slug}`);
export const createService = (data) => api.post('/services', data);
export const updateService = (id, data) => api.put(`/services/${id}`, data);
export const deleteService = (id) => api.delete(`/services/${id}`);

// Testimonials
export const getTestimonials = (params) => api.get('/testimonials', { params });
export const createTestimonial = (data) => api.post('/testimonials', data);
export const updateTestimonial = (id, data) => api.put(`/testimonials/${id}`, data);
export const deleteTestimonial = (id) => api.delete(`/testimonials/${id}`);

// Blogs
export const getBlogs = (params) => api.get('/blogs', { params });
export const getBlogBySlug = (slug) => api.get(`/blogs/${slug}`);
export const incrementBlogViews = (slug) => api.patch(`/blogs/${slug}/view`);
export const createBlog = (data) => api.post('/blogs', data);
export const updateBlog = (id, data) => api.put(`/blogs/${id}`, data);
export const deleteBlog = (id) => api.delete(`/blogs/${id}`);

// Contacts
export const submitContact = (data) => api.post('/contacts', data);
export const getContacts = (params) => api.get('/contacts', { params });
export const updateContactStatus = (id, status) =>
  api.patch(`/contacts/${id}/status`, { status });
export const deleteContact = (id) => api.delete(`/contacts/${id}`);
export const getContactStats = () => api.get('/contacts/stats');

// Upload
export const uploadImage = (formData) =>
  api.post('/upload/image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

export const uploadImages = (formData) =>
  api.post('/upload/images', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

export const deleteUploadedFile = (filename) => api.delete(`/upload/${filename}`);

// Team
export const getTeamMembers = (params) => api.get('/team', { params }).then(res => res.data);
export const getTeamMember = (id) => api.get(`/team/${id}`).then(res => res.data);
export const createTeamMember = (data) => api.post('/team', data).then(res => res.data);
export const updateTeamMember = (id, data) => api.put(`/team/${id}`, data).then(res => res.data);
export const deleteTeamMember = (id) => api.delete(`/team/${id}`).then(res => res.data);

// Careers
export const getCareers = (params) => api.get('/careers', { params }).then(res => res.data);
export const getCareer = (slug) => api.get(`/careers/${slug}`).then(res => res.data);
export const incrementCareerViews = (slug) => api.patch(`/careers/${slug}/view`).then(res => res.data);
export const createCareer = (data) => api.post('/careers', data).then(res => res.data);
export const updateCareer = (id, data) => api.put(`/careers/${id}`, data).then(res => res.data);
export const deleteCareer = (id) => api.delete(`/careers/${id}`).then(res => res.data);
